import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";

import { queryClient } from "@/config/react-query";

import { useTimezone } from "@/hooks/use-timezone";

import type {
  BlastChannel,
  BlastCreatePayload,
  BlastDetail,
  BlastFetchParams,
  BlastRecipientSelection,
  BlastSummary,
  BlastTestSendRequest,
  EmailVerificationSummary,
} from "@/types/blast";
import type {
  CohortCreatePayload,
  CohortFetchParams,
  CohortFilters,
  CohortUpdatePayload,
} from "@/types/cohort";

import {
  type BlastAnalyticsFilterParams,
  type BlastMessagesFilters,
  addBlastRecipients,
  cancelScheduledBlast,
  checkCohortDuplicate,
  createBlast,
  createCohort,
  deleteCohort,
  duplicateBlast,
  estimateCost,
  fetchBlasts,
  fetchCohortOptions,
  fetchCohortPatientCount,
  fetchCohorts,
  fetchVerificationSummary,
  getBlast,
  getBlastAnalytics,
  getBlastEngagement,
  getBlastMessages,
  getCohort,
  getCohortPatients,
  previewBlast,
  previewBlastRecipients,
  previewCohortCount,
  previewCustomContacts,
  previewEmail,
  rescheduleBlast,
  retryBlast,
  startVerification,
  stopBlast,
  testSendBlast,
  updateCohort,
  uploadBlastFiles,
} from "@/api/provider/comms/blast";
import { patientQueryKeys } from "@/api/provider/user/patient/patient-query";

// ─── Query Keys ──────────────────────────────────────────────────────────────

export const blastQueryKeys = {
  all: ["blasts"] as const,
  list: (params?: BlastFetchParams) =>
    [...blastQueryKeys.all, "list", params] as const,
  detail: (id: string) => [...blastQueryKeys.all, "detail", id] as const,
  messages: (
    id: string,
    page?: number,
    filters?: BlastMessagesFilters,
    status?: string,
  ) => [...blastQueryKeys.all, "messages", id, page, filters, status] as const,
  analytics: (
    from: string,
    to: string,
    filters?: BlastAnalyticsFilterParams,
    timezone?: string,
  ) =>
    [...blastQueryKeys.all, "analytics", from, to, filters, timezone] as const,
  engagement: (id: string, status?: string) =>
    [...blastQueryKeys.all, "engagement", id, status] as const,
  previewRecipients: (
    selection: BlastRecipientSelection,
    page: number,
    channel: BlastChannel,
    isMarketing: boolean,
    search: string,
    sendableOnly: boolean,
    includeNotOptedIn: boolean,
  ) =>
    [
      ...blastQueryKeys.all,
      "preview-recipients",
      selection,
      page,
      channel,
      isMarketing,
      search,
      sendableOnly,
      includeNotOptedIn,
    ] as const,
  // Sort here, not at the call site: the backend request_key is order-independent,
  // so the cache key must be too. Normalizing inside the helper means invalidation
  // callers (the verify button) can't miss by passing an unsorted list.
  verificationSummary: (selection: BlastRecipientSelection) =>
    [
      ...blastQueryKeys.all,
      "verification-summary",
      [...selection.cohortIds].sort(),
      [...selection.patientIds].sort(),
      [...selection.excludedPatientIds].sort(),
    ] as const,
};

export const cohortQueryKeys = {
  all: ["cohorts"] as const,
  list: (params?: CohortFetchParams) =>
    [...cohortQueryKeys.all, "list", params] as const,
  detail: (id: string) => [...cohortQueryKeys.all, "detail", id] as const,
  patientCount: (cohortIds: string[]) =>
    [...cohortQueryKeys.all, "patient-count", [...cohortIds].sort()] as const,
  patients: (
    id: string,
    page?: number,
    channel?: string,
    isMarketing?: boolean,
  ) =>
    [
      ...cohortQueryKeys.all,
      "patients",
      id,
      page,
      channel,
      isMarketing,
    ] as const,
};

// ─── Blast Hooks ─────────────────────────────────────────────────────────────

export function useBlasts(params: BlastFetchParams = {}) {
  return useQuery({
    queryKey: blastQueryKeys.list(params),
    queryFn: () => fetchBlasts(params),
  });
}

export function useBlast(blastId: string) {
  return useQuery({
    queryKey: blastQueryKeys.detail(blastId),
    queryFn: () => getBlast(blastId),
    enabled: !!blastId,
    // Attachment signed URLs expire after 60 min — refetch before they go stale
    staleTime: 50 * 60 * 1000, // 50 min
    // Poll every 3s while the blast waits for its send worker or is sending.
    // A send or Add Recipients leaves it PENDING until the worker picks it up.
    // A scheduled blast checks back around its send time to see it start.
    refetchInterval: (query) => {
      const blast = query.state.data;
      if (blast?.status === "PENDING" || blast?.status === "SENDING") {
        return 3000;
      }
      if (blast?.status === "SCHEDULED" && blast.scheduledFor) {
        const untilDue = new Date(blast.scheduledFor).getTime() - Date.now();
        return Math.min(Math.max(untilDue, 30_000), 15 * 60_000);
      }
      return false;
    },
  });
}

// False until the blast has loaded, and for a scheduled or cancelled blast,
// which has queued no one: there is no engagement to fetch yet.
function hasStartedSending(status?: string): boolean {
  return !!status && status !== "SCHEDULED" && status !== "CANCELLED";
}

// Messages and engagement are keyed by the blast's status, so they load fresh
// when a scheduled blast starts or a send finishes, rather than keeping what
// was cached before.
export function useBlastMessages(
  blastId: string,
  page: number = 1,
  filters: BlastMessagesFilters = {},
  blastStatus?: string,
) {
  return useQuery({
    queryKey: blastQueryKeys.messages(blastId, page, filters, blastStatus),
    queryFn: () => getBlastMessages(blastId, page, 25, filters),
    // Waits for the blast to load. A scheduled blast lists who is in its audience
    // now; a cancelled one never sent to anyone.
    enabled: !!blastId && !!blastStatus && blastStatus !== "CANCELLED",
    // Poll every 3s while blast is actively sending so messages table updates live
    refetchInterval: blastStatus === "SENDING" ? 3000 : false,
  });
}

export function useCreateBlast() {
  return useMutation({
    mutationFn: (payload: BlastCreatePayload) => createBlast(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: blastQueryKeys.all });
    },
  });
}

export function useRetryBlast() {
  return useMutation({
    mutationFn: (blastId: string) => retryBlast(blastId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: blastQueryKeys.all });
    },
  });
}

export function useRescheduleBlast() {
  return useMutation({
    mutationFn: ({
      blastId,
      scheduledFor,
    }: {
      blastId: string;
      scheduledFor: Date;
    }) => rescheduleBlast(blastId, scheduledFor),
    onSuccess: applyScheduleChange,
  });
}

export function useCancelScheduledBlast() {
  return useMutation({
    mutationFn: (blastId: string) => cancelScheduledBlast(blastId),
    onSuccess: applyScheduleChange,
  });
}

// Copies only the fields a reschedule or cancel changes: the response leaves the
// cohort names null, so spreading all of it would blank them on the detail page.
// A detail poll already in flight is cancelled first, or its older answer would
// land after this and put the blast back to its pre-change state.
// The list is cached per filter and page, and a cancelled blast leaves the
// Scheduled filter, so the server redoes the paging there.
async function applyScheduleChange(summary: BlastSummary | null) {
  if (summary) {
    const detailKey = blastQueryKeys.detail(summary.id);
    await queryClient.cancelQueries({ queryKey: detailKey });
    queryClient.setQueryData<BlastDetail>(
      detailKey,
      (old) =>
        old && {
          ...old,
          status: summary.status,
          scheduledFor: summary.scheduledFor,
          completedAt: summary.completedAt,
        },
    );
  }
  queryClient.invalidateQueries({ queryKey: [...blastQueryKeys.all, "list"] });
}

export function useStopBlast() {
  return useMutation({
    mutationFn: (blastId: string) => stopBlast(blastId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: blastQueryKeys.all });
    },
  });
}

export function usePreviewBlast() {
  return useMutation({
    mutationFn: previewBlast,
  });
}

export function usePreviewBlastRecipients(
  selection: BlastRecipientSelection,
  page: number,
  channel: BlastChannel,
  isMarketing: boolean,
  search: string = "",
  sendableOnly: boolean = false,
  includeNotOptedIn: boolean = false,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: blastQueryKeys.previewRecipients(
      selection,
      page,
      channel,
      isMarketing,
      search,
      sendableOnly,
      includeNotOptedIn,
    ),
    queryFn: () =>
      previewBlastRecipients(
        { channel, isMarketing, ...selection },
        page,
        25,
        search || undefined,
        sendableOnly,
        includeNotOptedIn,
      ),
    enabled:
      enabled &&
      (selection.cohortIds.length > 0 || selection.patientIds.length > 0),
    placeholderData: keepPreviousData,
  });
}

export function usePreviewEmail() {
  return useMutation({
    mutationFn: previewEmail,
  });
}

// True when the summary still describes verification in progress — either a
// live count or an active job. Shared by the hook (to decide whether a reopen
// must re-sync) and the bar (to detect the in-flight→done edge). Kept here, not
// in the component, because the summary key is shared with the send-dialog "Not
// verified" callout, so both surfaces must judge "in flight" identically.
export const isVerificationInFlight = (
  s?: EmailVerificationSummary | null,
): boolean =>
  (s?.verifyingCount ?? 0) > 0 || (s?.activeJobIds?.length ?? 0) > 0;

export function useVerificationSummary(
  selection: BlastRecipientSelection,
  enabled: boolean,
) {
  // verificationSummary normalizes cohort-id order, so the button and the
  // send-dialog callout de-dupe to one fetch even if selection order differs.
  //
  // No timer: completion is push-driven. The Mailgun validation-job webhook ingests results
  // server-side and broadcasts EMAIL_VERIFICATION_COMPLETE, which the bar consumes to refetch
  // this summary. Reopening the dialog / reloading refetches on mount if a push is missed
  // (no data is lost — verdicts are already in the DB).
  return useQuery({
    queryKey: blastQueryKeys.verificationSummary(selection),
    queryFn: () => fetchVerificationSummary(selection),
    enabled:
      enabled &&
      (selection.cohortIds.length > 0 || selection.patientIds.length > 0),
    // Conditional, not a blanket staleTime: 0. Only force a re-sync on reopen when the
    // cached state still claims "verifying" — the exact case that can be stale-stuck if a
    // completion push was missed. Idle/steady-state reopens behave like today (honor
    // staleTime, no added request).
    refetchOnMount: (query) =>
      isVerificationInFlight(query.state.data) ? "always" : true,
  });
}

export function useStartVerification() {
  return useMutation({
    mutationFn: (selection: BlastRecipientSelection) =>
      startVerification(selection),
  });
}

export function useUploadBlastFiles() {
  return useMutation({
    mutationFn: ({
      files,
      channel,
    }: {
      files: File[];
      channel: "SMS" | "EMAIL";
    }) => uploadBlastFiles(files, channel),
  });
}

export function useEstimateCost() {
  return useMutation({
    mutationFn: estimateCost,
  });
}

export function useTestSendBlast() {
  return useMutation({
    mutationFn: (payload: BlastTestSendRequest) => testSendBlast(payload),
  });
}

export function useBlastEngagement(blastId: string, blastStatus?: string) {
  return useQuery({
    queryKey: blastQueryKeys.engagement(blastId, blastStatus),
    queryFn: () => getBlastEngagement(blastId),
    enabled: !!blastId && hasStartedSending(blastStatus),
  });
}

export function useBlastAnalytics(
  from: string,
  to: string,
  filters?: BlastAnalyticsFilterParams,
) {
  const timezone = useTimezone();
  return useQuery({
    queryKey: blastQueryKeys.analytics(from, to, filters, timezone),
    queryFn: () => getBlastAnalytics(from, to, filters, timezone),
  });
}

export function useAddBlastRecipients() {
  return useMutation({
    mutationFn: ({
      blastId,
      patientIds,
    }: {
      blastId: string;
      patientIds: string[];
    }) => addBlastRecipients(blastId, patientIds),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: blastQueryKeys.detail(variables.blastId),
      });
      queryClient.invalidateQueries({
        queryKey: blastQueryKeys.messages(variables.blastId),
      });
    },
  });
}

export function useDuplicateBlast() {
  return useMutation({
    mutationFn: (blastId: string) => duplicateBlast(blastId),
  });
}

// ─── Cohort Hooks ────────────────────────────────────────────────────────────

export function useCohorts(params: CohortFetchParams = {}) {
  return useQuery({
    queryKey: cohortQueryKeys.list(params),
    queryFn: () => fetchCohorts(params),
  });
}

export function useCohortOptions() {
  return useQuery({
    queryKey: [...cohortQueryKeys.all, "options"] as const,
    queryFn: () => fetchCohortOptions(),
  });
}

export function useCohort(cohortId: string) {
  return useQuery({
    queryKey: cohortQueryKeys.detail(cohortId),
    queryFn: () => getCohort(cohortId),
    enabled: !!cohortId,
  });
}

export function useCohortPatients(
  cohortId: string,
  page: number = 1,
  channel?: string,
  isMarketing?: boolean,
) {
  return useQuery({
    queryKey: cohortQueryKeys.patients(cohortId, page, channel, isMarketing),
    queryFn: () => getCohortPatients(cohortId, page, 25, channel, isMarketing),
    enabled: !!cohortId,
  });
}

export function useCreateCohort() {
  return useMutation({
    mutationFn: ({
      payload,
      senderId,
    }: {
      payload: CohortCreatePayload;
      senderId: string;
    }) => createCohort(payload, senderId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: cohortQueryKeys.all,
      });
    },
  });
}

export function useUpdateCohort() {
  return useMutation({
    mutationFn: ({
      cohortId,
      payload,
    }: {
      cohortId: string;
      payload: CohortUpdatePayload;
    }) => updateCohort(cohortId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: cohortQueryKeys.all,
      });
      // The cohort page lists members through the patient table, and its
      // Select all reads them from the ids query, both keyed apart from the
      // cohort. New criteria need both refetched, or a bulk archive or tag
      // after an edit goes to the old membership.
      queryClient.invalidateQueries({
        queryKey: [...patientQueryKeys.all, "table"],
      });
      queryClient.invalidateQueries({
        queryKey: [...patientQueryKeys.all, "ids"],
      });
    },
  });
}

export function useCohortPatientCount(cohortIds: string[]) {
  return useQuery({
    queryKey: cohortQueryKeys.patientCount(cohortIds),
    queryFn: () => fetchCohortPatientCount(cohortIds),
    enabled: cohortIds.length > 0,
  });
}

export function useDeleteCohort() {
  return useMutation({
    mutationFn: (cohortId: string) => deleteCohort(cohortId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: cohortQueryKeys.all,
      });
    },
  });
}

export function useCheckCohortDuplicate() {
  return useMutation({
    mutationFn: ({
      filterCriteria,
      excludeCohortId,
    }: {
      filterCriteria: CohortFilters;
      excludeCohortId?: string;
    }) => checkCohortDuplicate(filterCriteria, excludeCohortId),
  });
}

export function usePreviewCohortCount() {
  return useMutation({
    mutationFn: ({
      filterCriteria,
      tz,
    }: {
      filterCriteria: CohortFilters;
      tz?: string;
    }) => previewCohortCount(filterCriteria, tz),
  });
}

/** Preview which patients match a freeform contact list. The caller is expected
 * to debounce its input (the raw text) before tokenizing and invoking.
 *
 * The full `contacts` array is the query key: React Query serializes it
 * deterministically, so the cache never collides two different lists (a hash
 * key could, and a collision would persist the wrong server-normalized
 * contacts onto the cohort). */
export function useCohortCustomContactsPreview(contacts: string[]) {
  return useQuery({
    queryKey: ["cohorts", "preview-custom-contacts", contacts],
    queryFn: () => previewCustomContacts(contacts),
    enabled: contacts.length > 0,
    staleTime: 30_000,
  });
}
